import { ENTITY_TYPES } from 'local/common/dist'
import { DraftLead } from '../draftLeads'
import { ILeadDetail } from '../lead'
import { IOpportunityDetail } from '../opportunity'
import {
  EntityLocations,
  IContact,
  IReferral,
  IUser,
  KnockStatusDetailsDto,
  LocationDTO,
} from '@sales-center/common'
import { AddressLocation } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import {
  KnockStatusHistoryRecord,
  KnockStatusLocationByCoordinatesResult,
} from '@user/store/knockStatus/typings'

export type LatLng = { lat: number; lng: number }

// The searched-region boundary (zip/county/city/state) drawn as a polygon
// outline on the map after a place search. `rings` are the raw polygon rings
// from /mapgeodata/boundary; nested rings are treated as holes when rendered.
export type SearchAreaBoundary = {
  rings: LatLng[][]
  featureName?: string | null
  featureId?: string | null
}

export type SearchAreaResponse = {
  total: number
  myRecords: number
  totalLead: number
  totalOpportunity: number
  totalDraftLead: number
  totalPestPacLocations?: number
  myLead: number
  myOpportunity: number
  myDraftLead: number
  locations: EntityLocationDTO[]
  filters: string
  displayedPinCount: number
  /**
   * Client-only field, not sent by the backend. Attached by getContactLocationsInArea when
   * overrideFilters is true and a real knock-status filter is active — the real knockStatusIds
   * resolved to IKnockStatus.name values, for the reducer to re-apply as a client-side filter
   * (SH-2100, parallel to the entity-type re-filter).
   */
  filteredKnockStatusNames?: string[]
}

export type PestPacLocationDetailsDTO = {
  locationId?: string | null
  locationCode?: string | null
  branchId?: string | null
  branch?: string | null
  billToId?: string | null
  firstName?: string | null
  lastName?: string | null
  company?: string | null
  phone?: string | null
  phoneExt?: string | null
  mobile?: string | null
  mobileExt?: string | null
  altPhone?: string | null
  altPhoneExt?: string | null
  email?: string | null
  locationTypeId?: string | null
  locationType?: string | null
  divisionId?: string | null
  division?: string | null
}

export type PestPacKnockStatusDTO = {
  /** Id of the underlying KnockStatusHistory row (SH-1200), needed to edit the reason. */
  id?: number | null
  knockStatusLocationId?: number | null
  knockStatusName?: string | null
  reason?: string | null
  createdTime?: string | null
  createdByName?: string | null
  knockStatusCount?: number | null
}

export type PestPacLocationDetailResponseDTO = {
  locationId?: string | null
  locationCode?: string | null
  branchId?: string | null
  branch?: string | null
  billToId?: string | null
  firstName?: string | null
  lastName?: string | null
  company?: string | null
  phone?: string | null
  phoneExt?: string | null
  mobile?: string | null
  mobileExt?: string | null
  altPhone?: string | null
  altPhoneExt?: string | null
  email?: string | null
  locationTypeId?: string | null
  locationType?: string | null
  divisionId?: string | null
  division?: string | null
  address?: string | null
  address2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  country?: string | null
  latitude?: number | null
  longitude?: number | null
  rooftopLatitude?: number | null
  rooftopLongitude?: number | null
  formattedAddress?: string | null
  serviceLocationName?: string | null
  knockStatus?: PestPacKnockStatusDTO | null
}

export type SearchResponse<T> = {
  items: T[]
  count: number
  skip: number
  take: number
}

export type ServicePlanModel = {
  id: number
  servicePlanType?: string | null
  setupType?: string | null
  name: string
  recurrence?: string | null
  targets?: string[] | null
  services?: any[] | null
  price?: number | null
  duration?: number | null
  startDate?: string | null
  renewalDate?: string | null
  lastServiceDate?: string | null
  lastServiceId?: number | null
  lastServiceNumber?: string | null
  lastServiceServiceOrderOrInvoice?: string | null
  nextServiceDate?: string | null
  nextServiceId?: number | null
  nextServiceNumber?: string | null
  isOnAutoBill: boolean
  /**
   * Service status label — not populated by the API today; status is derived
   * client-side from active/cancelDate/the dates above (see serviceStatus.ts).
   */
  status?: string | null
  active?: boolean | null
  cancelDate?: string | null
  cancelReason?: string | null
}

export type EntityLocationDTO = {
  boundingBox?: {
    long1: number
    lat1: number
    long2: number
    lat2: number
  }
  count: number
  entityId: number
  entityType: string
  latitude: number
  longitude: number
  multitenant: boolean
  serviceLocationId?: number
  pinId: string
  entities: MultiTenantLocation[]
  formattedAddress?: string
  assigneeId?: string
  street?: string
  houseNumber?: string
  street1?: string
  street2?: string
  city?: string
  stateAbbreviation?: string
  postalCode?: string
  country?: string
  county?: string
  knockStatusSettingColor?: string | null
  knockStatusSettingIcon?: number | null
  knockStatusLocationId?: number | null
  contactLocationId?: number
  address?: AddressLocation
  knockStatusReason?: string
  knockStatusSettingName?: string
  knockStatusCreatedTime?: string | null
  knockStatusCreatedByName?: string | null
  knockStatusCount?: number | null
  // SH-1307 -- full history for this location, so "View Knock Status History" can
  // show even when there's no longer an active status (deleted from Settings).
  knockStatusHistory?: KnockStatusHistoryRecord[]
  /** Id of the underlying KnockStatusHistory row (SH-1200). */
  knockStatusHistoryId?: number | null
  pestPacDetails?: PestPacLocationDetailsDTO | null
  pestPacLocationId?: string | null
  isClosed?: boolean
  isLost?: boolean
}

/**
 * Slim projection of EntityLocationDTO used by the tile cache path.
 * Only fields that are actually read during map rendering or pin-click bubble
 * display are included. Fields omitted here (assigneeId, boundingBox, address)
 * can be safely dropped from the SearchArea backend response without breaking
 * any map functionality.
 */
export type MapPinDTO = {
  pinId: string
  entityId: number
  entityType: string
  latitude: number
  longitude: number
  count: number
  multitenant: boolean
  serviceLocationId?: number
  contactLocationId?: number
  assigneeId?: string
  entities: MultiTenantLocation[]
  knockStatusSettingColor?: string | null
  knockStatusSettingIcon?: number | null
  knockStatusSettingName?: string
  knockStatusLocationId?: number | null
  knockStatusReason?: string
  knockStatusCreatedTime?: string | null
  knockStatusCreatedByName?: string | null
  knockStatusCount?: number | null
  /** Id of the underlying KnockStatusHistory row (SH-1200). */
  knockStatusHistoryId?: number | null
  formattedAddress?: string
  street?: string
  houseNumber?: string
  street1?: string
  street2?: string
  city?: string
  stateAbbreviation?: string
  postalCode?: string
  country?: string
  county?: string
  pestPacDetails?: PestPacLocationDetailsDTO | null
  pestPacLocationId?: string | null
  isClosed?: boolean
  isLost?: boolean
}

export type EntityDetail =
  | (Omit<ILeadDetail, 'entityType'> & {
      entityType: ENTITY_TYPES.Lead
      nextActionDate?: Date | null
      street1?: string
      street2?: string
      city?: string
      stateAbbreviation?: string
      postalCode?: string
      country?: string
      latitude?: number
      longitude?: number
      locations: EntityLocations
      knockStatusDetails?: KnockStatusDetailsDto
    })
  | (Omit<IOpportunityDetail, 'entityType'> & {
      entityType: ENTITY_TYPES.Opportunity
      nextActionDate?: Date | null
      street1?: string
      street2?: string
      city?: string
      stateAbbreviation?: string
      postalCode?: string
      country?: string
      latitude?: number
      longitude?: number
      locations: EntityLocations
      knockStatusDetails?: KnockStatusDetailsDto
    })
  | (Omit<DraftLead, 'entityType'> & {
      entityType: ENTITY_TYPES.DraftLead
      age?: number
      createdBy: IUser
      services: any[] | null
      assignee: IUser | null
      salesFunnelStage: string | null
      salesFunnel: string
      salesTeam: string
      serviceContact: IContact
      totalLeadValue: number
      externalLocationId: string
      servicesCount: number
      referralSummary: IReferral
      nextActionDate?: Date | null
      street1?: string
      street2?: string
      city?: string
      stateAbbreviation?: string
      postalCode?: string
      country?: string
      latitude?: number
      longitude?: number
      locations: EntityLocations
      knockStatusDetails?: KnockStatusDetailsDto
    })

/**
 * SH-1143 — A record present at an exact coordinate, returned by
 * GET /ContactLocations/recordsAtLocation UNFILTERED by the user's active map filters.
 * Used to detect records hidden by the current filter selections.
 */
export type RecordAtLocation = {
  entityType: string
  knockStatusSettingsId: number | null
  ownerId: string | null
  /**
   * Identifiers of the record behind this row, used to open its preview bubble when the clicked
   * location already has record(s). Nullable because the backend columns are, and optional so the
   * client degrades to the address bubble when running against a backend that predates them.
   */
  entityId?: number | null
  serviceLocationId?: number | null
  contactLocationId?: number | null
  latitude?: number | null
  longitude?: number | null
}

/**
 * SH-1143 — Response of GET /ContactLocations/recordsAtLocation. One coordinate lookup serving both
 * the hidden-by-filters detection (`records`) and the add-lead / knock-status preview flow
 * (`knockStatus`, null when the location has no knock-status history).
 */
export type RecordsAtLocationResponse = {
  records: RecordAtLocation[]
  knockStatus: KnockStatusLocationByCoordinatesResult | null
}

export type SalespersonFilterOption = {
  userId: string
  name: string
  isActive: boolean
  isAdmin: boolean
  hasMapActivity: boolean
}

export type MultiTenantLocation = {
  entityType: string
  entityId?: number
  serviceLocationId?: number
  contactLocationId?: number
  hasViewPermission?: boolean
  pestPacLocationId?: string | null
  pestPacDetails?: PestPacLocationDetailsDTO | null
  formattedAddress?: string | null
  street1?: string | null
  street2?: string | null
  city?: string | null
  stateAbbreviation?: string | null
  postalCode?: string | null
  country?: string | null
  latitude?: number | null
  longitude?: number | null
  knockStatusHistoryId?: number | null
  knockStatusLocationId?: number | null
  knockStatusSettingName?: string | null
  knockStatusSettingColor?: string | null
  knockStatusSettingIcon?: number | null
  knockStatusReason?: string | null
  knockStatusCreatedTime?: string | null
  knockStatusCreatedByName?: string | null
  knockStatusCount?: number | null
}

export type PestPacMultiTenantLocation = MultiTenantLocation & {
  pestPacLocationId: string
}

export function isPestPacMember(
  member: MultiTenantLocation,
): member is PestPacMultiTenantLocation {
  return (
    !!member.pestPacLocationId &&
    member.contactLocationId == null &&
    member.entityId == null
  )
}

export type SalesCenterMultiTenantLocation = MultiTenantLocation & {
  entityId: number
  serviceLocationId: number
  contactLocationId: number
}

export function salesCenterContactLocationIds(
  members: MultiTenantLocation[],
): number[] {
  return members
    .filter((member) => !isPestPacMember(member))
    .map((member) => member.contactLocationId)
    .filter((id): id is number => id != null)
}

export type MultiTenantLocationDetails = LocationDTO &
  SalesCenterMultiTenantLocation
